// src/store/apis/taxDealsApi.ts
import { createApi } from '@reduxjs/toolkit/query/react';
import { customBaseQuery } from '@/lib/baseQuery';
import { PropertyMetadata } from '@/pages/tax/routes/TaxWorkflow/types/tax-process.type';

export type TaxDealClientRole = 'primary' | 'spouse' | 'partner' | 'other';

const unwrap = <T>(res: unknown): T =>
  res && typeof res === 'object' && 'data' in res
    ? ((res as { data: unknown }).data as T)
    : (res as T);

export interface DealClient {
  id: string;
  role: TaxDealClientRole;
  client: {
    id: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    phoneNumber?: string;
    hubspotId?: string | null;
  };
}

export interface TaxDeal {
  id: string;
  dealTitle: string;
  status: string;
  properties?: PropertyMetadata[];
  financialYearEnd?: number | null;
  yearlyReturns?: YearlyReturns[] | null;
  driveFolderId?: string | null;
  createdAt?: string;
  updatedAt?: string;
  taxSubmissions?: Record<string, unknown>[];
  dealClients?: DealClient[];
  sheetId?: string;
}

export interface YearlyReturns {
  id: string;
  createdAt: string;
  updatedAt: string;
  status: string;
  meta: Record<string, unknown>;
  financialYearEnd: number;
  /**
   * Amendment revision within the financial year — 0 is the original return.
   * A year can hold several of these; the highest one is the live return.
   */
  amendedVersion?: number;
  hubspotDealId?: string | null;
}

export interface CreateClientRequest {
  email: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;

  sourceUserId: string;
  sourceSystem: string;

  initialService: string;
}

/**
 * Ownership split as the backend's PropertyOwnershipDto expects it. Note the
 * key names — `clientId`/`ownershipPercent`, not the form's
 * `ownerKey`/`percentage`.
 */
export type PropertyOwnershipPayload = {
  clientId: string;
  ownershipPercent: number;
};

/**
 * Mirror of the backend `PropertyDetailsPayload`
 * (property/dto/create-property.dto.ts). Worth keeping in step by hand: the
 * global ValidationPipe runs with `whitelist: true` and no
 * `forbidNonWhitelisted`, so a key that isn't on the DTO is dropped silently
 * — a 200 response with the field never saved. The index signature keeps
 * genuinely open-ended keys legal without giving up checking on these.
 */
export type TaxPropertyDetailsPayload = Record<string, unknown> & {
  address?: string | null;
  ownership?: string | null;
  purchasePrice?: string | number | null;
  purchaseDate?: string | null;
  saleDate?: string | null;
  postcode?: string | null;
  state?: string | null;
  suburb?: string | null;
  rentalStartDate?: string | null;
  ownerships?: PropertyOwnershipPayload[];
};

/** One element of the deal-properties write body — backend CreatePropertyDto. */
export type TaxDealPropertyPayload = {
  clientId?: string;
  sourcePropertyId: string;
  propertyLocation: string;
  propertyDetails: TaxPropertyDetailsPayload;
  hubspotCustomObjectId?: string;
  hubspotStatus?: string;
};

export interface CreateTaxDealRequest {
  dealTitle: string;
  financialYearEnd?: number;
  clients: CreateClientRequest[];
  properties?: TaxDealPropertyPayload[];
  taxSubmissions?: Record<string, unknown>[];
  yearlyReturn?: {
    financialYearEnd: number;
    status: string;
    meta: Record<string, unknown>;
    assignedUserId?: number;
  };
}

export type CreateTaxDealResponse = { dealId: string };

export interface UpdateDealClientsRequest {
  id: string;
  clients: Array<{
    role: TaxDealClientRole;
    client: {
      email: string;
      firstName?: string;
      lastName?: string;
      phoneNumber?: string;
      sourceUserId: string;
      sourceSystem: string;
      initialService: string;
    };
  }>;
}

export type UpdateTaxDealPropertiesRequest = {
  id: string;
  properties: TaxDealPropertyPayload[];
};

export interface ListTaxDealsParams {
  page: number;
  limit: number;
  search?: string;
  /** Ordered on the server, across every page. */
  sort?: 'title' | 'updatedAt';
  dir?: 'asc' | 'desc';
}

export interface PaginatedTaxDeals {
  meta: { total: number; page: number; limit: number; pages: number };
  items: TaxDeal[];
}

export const taxDealsApi = createApi({
  reducerPath: 'taxDealsApi',
  baseQuery: customBaseQuery,
  tagTypes: ['TaxDeals', 'TaxDeal'],
  endpoints: (builder) => ({
    listTaxDeals: builder.query<PaginatedTaxDeals, ListTaxDealsParams>({
      query: ({ page, limit, search, sort, dir }) => {
        const params = new URLSearchParams({
          page: String(page),
          limit: String(limit),
        });
        if (search) params.set('search', search);
        if (sort) params.set('sort', sort);
        if (dir) params.set('dir', dir);
        return { url: `/v1/tax/deals?${params.toString()}`, method: 'GET' };
      },
      transformResponse: (res) => unwrap<PaginatedTaxDeals>(res),
      providesTags: (result) =>
        result
          ? [
              ...result.items.map((d) => ({
                type: 'TaxDeal' as const,
                id: d.id,
              })),
              { type: 'TaxDeals' as const, id: 'LIST' },
            ]
          : [{ type: 'TaxDeals' as const, id: 'LIST' }],
    }),

    getTaxDealById: builder.query<TaxDeal, { id: string }>({
      query: ({ id }) => ({ url: `/v1/tax/deals/${id}`, method: 'GET' }),
      transformResponse: (res) => unwrap<TaxDeal>(res),
      providesTags: (_res, _err, arg) => [{ type: 'TaxDeal', id: arg.id }],
    }),

    createTaxDeal: builder.mutation<
      CreateTaxDealResponse,
      CreateTaxDealRequest
    >({
      query: (body) => ({
        url: `/v1/tax/deals`,
        method: 'POST',
        body,
      }),
      transformResponse: (res) => unwrap<CreateTaxDealResponse>(res),
      invalidatesTags: [{ type: 'TaxDeals', id: 'LIST' }],
    }),

    updateTaxDealClients: builder.mutation<TaxDeal, UpdateDealClientsRequest>({
      query: ({ id, clients }) => ({
        url: `/v1/tax/deals/${id}/clients`,
        method: 'PUT',
        body: { clients },
      }),
      transformResponse: (res) => unwrap<TaxDeal>(res),
      invalidatesTags: (_res, _err, arg) => [
        { type: 'TaxDeal', id: arg.id },
        { type: 'TaxDeals', id: 'LIST' },
      ],
    }),

    updateTaxDealProperties: builder.mutation<
      TaxDeal,
      UpdateTaxDealPropertiesRequest
    >({
      query: ({ id, properties }) => ({
        url: `/v1/tax/deals/${id}/properties`,
        method: 'PUT',
        body: { properties },
      }),
      transformResponse: (res) => unwrap<TaxDeal>(res),
      invalidatesTags: (_res, _err, arg) => [
        { type: 'TaxDeal', id: arg.id },
        { type: 'TaxDeals', id: 'LIST' },
      ],
    }),

    updateTaxDealTitle: builder.mutation<
      TaxDeal,
      { id: string; dealTitle: string }
    >({
      query: ({ id, dealTitle }) => ({
        url: `/v1/tax/deals/rename/${id}/title`,
        method: 'PUT',
        body: { dealTitle },
      }),
      transformResponse: (res) => unwrap<TaxDeal>(res),
      invalidatesTags: (_res, _err, arg) => [
        { type: 'TaxDeal', id: arg.id },
        { type: 'TaxDeals', id: 'LIST' },
      ],
    }),

    // Drive folder id or a pasted Drive URL; null clears the link
    updateTaxDealDriveFolder: builder.mutation<
      TaxDeal,
      { id: string; driveFolderId: string | null }
    >({
      query: ({ id, driveFolderId }) => ({
        url: `/v1/tax/deals/${id}/drive-folder`,
        method: 'PATCH',
        body: { driveFolderId },
      }),
      // The endpoint now answers { deal, preview } — it validates the folder
      // and reports what it adopted. Callers here only want the deal.
      transformResponse: (res) =>
        unwrap<{ deal: TaxDeal; preview: unknown }>(res).deal,
      invalidatesTags: (_res, _err, arg) => [
        { type: 'TaxDeal', id: arg.id },
        { type: 'TaxDeals', id: 'LIST' },
      ],
    }),

    // HubSpot deal id or a pasted record URL; null clears the link.
    // Omitting financialYearEnd applies it to every yearly return on the deal.
    updateTaxDealHubspotDeal: builder.mutation<
      TaxDeal,
      { id: string; hubspotDealId: string | null; financialYearEnd?: number }
    >({
      query: ({ id, hubspotDealId, financialYearEnd }) => ({
        url: `/v1/tax/deals/${id}/hubspot-deal`,
        method: 'PATCH',
        body: { hubspotDealId, ...(financialYearEnd && { financialYearEnd }) },
      }),
      transformResponse: (res) => unwrap<TaxDeal>(res),
      invalidatesTags: (_res, _err, arg) => [
        { type: 'TaxDeal', id: arg.id },
        { type: 'TaxDeals', id: 'LIST' },
      ],
    }),

    deleteTaxDeal: builder.mutation<void, { id: string }>({
      query: ({ id }) => ({
        url: `/v1/tax/deals/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_res, _err, arg) => [
        { type: 'TaxDeal', id: arg.id },
        { type: 'TaxDeals', id: 'LIST' },
      ],
    }),

    /**
     * Star/unstar a property for this tax deal. Unstarring only hides it from
     * the tax surfaces — the saved figures stay and come back if re-starred.
     */
    setPropertyTaxScope: builder.mutation<
      { taxDealId: string; propertyId: string; starred: boolean },
      { id: string; propertyId: string; starred: boolean }
    >({
      query: ({ id, propertyId, starred }) => ({
        url: `/v1/tax/deals/${id}/properties/${propertyId}/tax-scope`,
        method: 'PATCH',
        body: { starred },
      }),
      transformResponse: (res) =>
        unwrap<{ taxDealId: string; propertyId: string; starred: boolean }>(
          res,
        ),
      invalidatesTags: (_res, _err, arg) => [
        { type: 'TaxDeal', id: arg.id },
        { type: 'TaxDeals', id: 'LIST' },
      ],
    }),

    deleteTaxDealProperty: builder.mutation<
      TaxDeal,
      { id: string; propertyId: string }
    >({
      query: ({ id, propertyId }) => ({
        url: `/v1/tax/deals/${id}/properties/${propertyId}`,
        method: 'DELETE',
      }),
      transformResponse: (res) => unwrap<TaxDeal>(res),
      invalidatesTags: (_res, _err, arg) => [
        { type: 'TaxDeal', id: arg.id },
        { type: 'TaxDeals', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useListTaxDealsQuery,
  useGetTaxDealByIdQuery,
  useCreateTaxDealMutation,
  useUpdateTaxDealClientsMutation,
  useUpdateTaxDealPropertiesMutation,
  useUpdateTaxDealTitleMutation,
  useUpdateTaxDealDriveFolderMutation,
  useUpdateTaxDealHubspotDealMutation,
  useDeleteTaxDealMutation,
  useDeleteTaxDealPropertyMutation,
  useSetPropertyTaxScopeMutation,
} = taxDealsApi;
